import {
  AuthScopes,
  ClaimCenterBookableAndSchedule,
  ClaimCenterScheduleExceptions,
  ClaimCenterWithBookables,
  ClaimReservationResponse,
  ClaimReservationWithTimeslot,
  ScheduleException,
  UnschedulePreviewResponse,
} from '@Generated/back-office-client/src'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RootState } from '@AppState/redux-store'
import { useSelector } from 'react-redux'
import { Flex, IconButton, Separator, Theme, Text, Button, Em } from '@radix-ui/themes'
import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import { ScheduleUnavailability } from './schedule-unavailability'
import './style.scss'
import { ReservationGrid } from './reservation-grid'
import {
  convertDateToIsoDate,
  formatDateToDayString,
  formatWeekString,
  getDatesBetweenRange,
  getNearestDayOfWeek,
  getTodayAsUTC,
  isTodayVisible,
} from '../util/utils'
import { ReservationDetails } from './reservation-details'
import { chain } from 'lodash'
import { GridDisplayMode } from './types'
import { DateControl } from './date-control'
import { ActionMenu } from './action-menu'
import { GridItemProps } from '@interaction-gaming/planby'
import { isFriday, isMonday, isWithinInterval, parseISO } from 'date-fns'
import { ExceptionDetails } from '@Components/claims/exception-details.tsx'
import { useSearchParams } from 'react-router-dom'
import { ScheduleAppointment } from './schedule-appointment'
import { WithPermissions } from '@Components/common/with-permissions'
import { ClaimReservationsServiceAPI } from '@API/data-services/claim-reservations-service'
import { UsersServiceAPI } from '@API/data-services/users-service'
import { UnscheduleConfirmation } from './unschedule-confirmation'
import { ClaimFormDrawer } from './claim-form-drawer'
import { LinkAppointmentModal } from './link-appointment-modal'
import { PlayerSearchModal } from './player-search-modal'

export type ReservationsData = {
  exceptions: ClaimCenterScheduleExceptions[]
  reservations: ClaimReservationWithTimeslot[]
}

/**
 * Scrolls the planby grid to show a reservation at the given time.
 * Planby renders items in shadow DOM, so we can't query for them directly.
 * Instead we calculate the pixel position from the reservation's start time
 * using the same grid dimensions planby uses (hoursInDay * 600px, spread
 * across a startHour..endHour+2 time range). Polls for the scrollbox
 * since the grid may not be mounted yet when called.
 */
function scrollToReservation(
  startTimeIso: string,
  gridStartHour: number,
  gridEndHour: number,
  attempt = 0,
): Promise<void> {
  return new Promise((resolve) => {
    const scrollbox = document.getElementById('planby-layout-scrollbox')
    if (scrollbox) {
      const [hours, minutes] = startTimeIso.split(':').map(Number)
      const timeInHours = hours + minutes / 60
      const hoursInDay = gridEndHour - gridStartHour
      const totalTimeSpan = hoursInDay + 2
      const totalPixels = hoursInDay * 600
      const fraction = (timeInHours - gridStartHour) / totalTimeSpan
      const pixelPosition = fraction * totalPixels
      scrollbox.scrollTo({
        top: pixelPosition - scrollbox.clientHeight / 3,
        behavior: 'smooth',
      })
      setTimeout(resolve, 500)
    } else if (attempt < 50) {
      setTimeout(
        () =>
          scrollToReservation(startTimeIso, gridStartHour, gridEndHour, attempt + 1).then(resolve),
        200,
      )
    } else {
      resolve()
    }
  })
}

enum ClaimsQueryParams {
  MODE = 'mode',
  DATE = 'date',
  CLAIM_CENTER = 'claimCenter',
  OPEN_RESERVATION = 'openReservation',
}

export const ClaimsSchedule = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { activeCustomerId, currentUser } = useSelector((state: RootState) => state.users)
  const [gridDisplayMode, setGridDisplayMode] = useState<GridDisplayMode>(
    searchParams.get(ClaimsQueryParams.MODE)
      ? (searchParams.get(ClaimsQueryParams.MODE) as GridDisplayMode)
      : GridDisplayMode.DAY,
  )

  const dateFromUrl = searchParams.get(ClaimsQueryParams.DATE)
  const dateToUse = dateFromUrl ? parseISO(dateFromUrl) : getTodayAsUTC()
  const [startDate, setStartDate] = useState<Date>(
    gridDisplayMode === GridDisplayMode.WEEK
      ? getNearestDayOfWeek(dateToUse, isMonday, false)
      : dateToUse,
  )
  const [endDate, setEndDate] = useState<Date>(
    gridDisplayMode === GridDisplayMode.WEEK
      ? getNearestDayOfWeek(dateToUse, isFriday, true)
      : dateToUse,
  )

  const [startHour, setStartHour] = useState<number | null>(null)
  const [endHour, setEndHour] = useState<number | null>(null)
  const [activeClaimCenter, setActiveClaimCenter] = useState<ClaimCenterWithBookables | null>(null)

  const [selectedReservation, setSelectedReservation] =
    useState<ClaimReservationWithTimeslot | null>(null)
  const [selectedException, setSelectedException] = useState<ScheduleException | null>(null)
  const [showUnavailability, setShowUnavailability] = useState<boolean>(false)
  const [showScheduleAppointment, setShowScheduleAppointment] = useState<boolean>(false)
  const [unscheduleBookableId, setUnscheduleBookableId] = useState<string | null>(null)
  const [unschedulePreview, setUnschedulePreview] = useState<UnschedulePreviewResponse | null>(null)
  const [unscheduleError, setUnscheduleError] = useState<string | null>(null)
  const [isUnscheduleSubmitting, setIsUnscheduleSubmitting] = useState(false)
  const [drawerClaimFormId, setDrawerClaimFormId] = useState<string | null>(null)
  const [claimFormClaimantName, setClaimFormClaimantName] = useState<string>('')
  const [reopenReservationOnDrawerClose, setReopenReservationOnDrawerClose] =
    useState<ClaimReservationWithTimeslot | null>(null)
  const [linkModalState, setLinkModalState] = useState<{
    claimFormId: string
    currentReservationId: string | null
  } | null>(null)
  const [showPlayerSearch, setShowPlayerSearch] = useState<boolean>(false)

  const { data: claimCenters } = useQuery({
    queryKey: [`${activeCustomerId}-claim-centers`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return await ClaimReservationsServiceAPI.getClaimCenters()
    },
  })

  const startDateIso = convertDateToIsoDate(startDate)
  const endDateIso = convertDateToIsoDate(endDate)
  const datesForRange = getDatesBetweenRange(startDate, endDate)

  const {
    data: bookablesForDates,
    isLoading: isBookableLoading,
    refetch: refetchBookables,
  } = useQuery({
    queryKey: [`${activeClaimCenter?.id}-bookables-${startDateIso}-${endDateIso}`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      const requests = datesForRange.map((date) => {
        return ClaimReservationsServiceAPI.getClaimCenterBookablesForDate(
          activeClaimCenter!.id,
          date,
        )
      })
      return Promise.all(requests)
    },
    select(responseData: ClaimCenterBookableAndSchedule[][]) {
      return responseData.reduce(
        (acc, data, i) => {
          acc[datesForRange[i]] = data
          return acc
        },
        {} as Record<string, ClaimCenterBookableAndSchedule[]>,
      )
    },
    enabled: !!activeClaimCenter?.id && !!startDate,
  })

  useEffect(() => {
    if (!activeClaimCenter && claimCenters?.length) {
      const claimCenterFromUrl = searchParams.get(ClaimsQueryParams.CLAIM_CENTER)

      if (claimCenterFromUrl) {
        const matchingClaimCenter = claimCenters.find((c) => c.name === claimCenterFromUrl)
        setActiveClaimCenter(matchingClaimCenter ?? claimCenters[0])
      } else if (currentUser?.metadata.defaultClaimCenterId) {
        const matchingClaimCenter = claimCenters.find(
          (c) => c.id === currentUser?.metadata.defaultClaimCenterId,
        )
        setActiveClaimCenter(matchingClaimCenter ?? claimCenters[0])
      } else {
        setActiveClaimCenter(claimCenters[0])
      }
    }
  }, [activeClaimCenter, claimCenters, currentUser?.metadata.defaultClaimCenterId, searchParams])

  useEffect(() => {
    if (bookablesForDates) {
      const minStart =
        chain(Object.values(bookablesForDates))
          .flatten()
          .flatMap((b) => b.schedules)
          .minBy((s) => s.localStartTime)
          .value()?.localStartTime || '0'

      const maxEnd =
        chain(Object.values(bookablesForDates))
          .flatten()
          .flatMap((b) => b.schedules)
          .maxBy((s) => s.localEndTime)
          .value()?.localEndTime || '24'

      setStartHour(parseInt(minStart.split(':')[0]))

      setEndHour(parseInt(maxEnd.split(':')[0]))
    }
  }, [bookablesForDates])

  const scheduleQueryKey = [
    `${activeCustomerId}-${activeClaimCenter?.id}-${startDateIso}-${endDateIso}-schedule`,
  ]

  const {
    data: reservationsData,
    isLoading: isReservationLoading,
    refetch: refetchGrid,
  } = useQuery({
    queryKey: scheduleQueryKey,
    refetchInterval: 30000,
    queryFn: async () => {
      const reservations = await ClaimReservationsServiceAPI.getReservations(
        activeClaimCenter!.id,
        startDateIso,
        endDateIso,
      )

      const requests = datesForRange.map((date) => {
        return ClaimReservationsServiceAPI.getClaimCenterExceptionsForDate(
          activeClaimCenter!.id,
          date,
        )
      })

      const exceptions = await Promise.all(requests)

      return { exceptions, reservations }
    },
    enabled: !!activeClaimCenter?.id,
  })

  // Auto-open reservation details when navigated back from claim form linking.
  const [pendingOpenReservationId, setPendingOpenReservationId] = useState(() => {
    const id = searchParams.get(ClaimsQueryParams.OPEN_RESERVATION)
    if (id) {
      searchParams.delete(ClaimsQueryParams.OPEN_RESERVATION)
      setSearchParams(searchParams, { replace: true })
    }
    return id
  })

  useEffect(() => {
    if (!pendingOpenReservationId || !activeClaimCenter?.id) {
      return
    }
    refetchGrid().then(async ({ data }) => {
      const reservation = data?.reservations.find((r) => r.id === pendingOpenReservationId)
      if (reservation && startHour !== null && endHour !== null) {
        await scrollToReservation(reservation.timeslot.display.isoStartTime, startHour, endHour)
        setSelectedReservation(reservation)
      } else if (reservation) {
        setSelectedReservation(reservation)
      }
      setPendingOpenReservationId(null)
    })
  }, [pendingOpenReservationId, activeClaimCenter?.id, refetchGrid, startHour, endHour])

  const queryClient = useQueryClient()

  const onReservationUpdated = (updated: ClaimReservationWithTimeslot) => {
    setSelectedReservation(updated)
    queryClient.setQueryData(scheduleQueryKey, (old: typeof reservationsData) => {
      if (!old) {
        return old
      }
      return {
        ...old,
        reservations: old.reservations.map((r) => (r.id === updated.id ? updated : r)),
      }
    })
  }

  const onReservationRemoved = (reservationId: string) => {
    setSelectedReservation(null)
    queryClient.setQueryData(scheduleQueryKey, (old: typeof reservationsData) => {
      if (!old) {
        return old
      }
      return {
        ...old,
        reservations: old.reservations.filter((r) => r.id !== reservationId),
      }
    })
  }

  const { mutate: scheduleBookable } = useMutation({
    mutationFn: () => {
      return ClaimReservationsServiceAPI.scheduleBookableForDate(
        activeClaimCenter!.id,
        convertDateToIsoDate(startDate), // Only shown if DAY mode
      )
    },
    onSuccess: () => {
      refetchBookables()
    },
  })

  const { mutate: changeDefaultClaimCenter } = useMutation({
    mutationFn: (claimCenterId: string) => {
      return UsersServiceAPI.setCurrentUserMeta({ defaultClaimCenterId: claimCenterId })
    },
  })

  const onDateRangeChange = (startDate: Date, endDate?: Date) => {
    setStartDate(startDate)
    const rangeEnd = endDate ?? startDate
    setEndDate(rangeEnd)

    if (isTodayVisible(startDate, rangeEnd)) {
      searchParams.delete(ClaimsQueryParams.DATE)
      setSearchParams(searchParams)
    } else {
      searchParams.set(ClaimsQueryParams.DATE, convertDateToIsoDate(startDate))
      setSearchParams(searchParams)
    }
  }

  const onDisplayModeChange = (newDisplayMode: GridDisplayMode) => {
    setGridDisplayMode(newDisplayMode)
    searchParams.set(ClaimsQueryParams.MODE, newDisplayMode)
    setSearchParams(searchParams)
  }

  const onClaimCenterChange = (claimCenter: ClaimCenterWithBookables) => {
    setActiveClaimCenter(claimCenter)
    searchParams.set(ClaimsQueryParams.CLAIM_CENTER, claimCenter.name)
    setSearchParams(searchParams)
    changeDefaultClaimCenter(claimCenter.id)
  }

  const removableBookables = useMemo(() => {
    if (!bookablesForDates || !datesForRange.length || gridDisplayMode !== GridDisplayMode.DAY) {
      return []
    }
    const currentDate = datesForRange[0]
    return bookablesForDates[currentDate].filter(
      (b) => b.schedules.length > 0 && b.schedules.every((s) => s.localStartDate === currentDate),
    )
  }, [bookablesForDates, datesForRange, gridDisplayMode])

  const canRemoveSlot = removableBookables.length > 0

  const handleRemoveAppointmentSlot = async () => {
    if (!activeClaimCenter || removableBookables.length === 0) {
      return
    }
    // removableBookables is ordered by sort_order ASC from the API, so the last element is the most recently added slot
    const newestSlot = removableBookables[removableBookables.length - 1]
    setUnscheduleError(null)
    try {
      const preview = await ClaimReservationsServiceAPI.getUnschedulePreview(
        activeClaimCenter.id,
        newestSlot.bookable.id,
        convertDateToIsoDate(startDate),
      )
      setUnscheduleBookableId(newestSlot.bookable.id)
      setUnschedulePreview(preview)
    } catch {
      setUnscheduleError('Failed to load slot details. Please try again.')
    }
  }

  const handleUnscheduleConfirm = async () => {
    if (!unscheduleBookableId || !activeClaimCenter || isUnscheduleSubmitting) {
      return
    }
    setUnscheduleError(null)
    setIsUnscheduleSubmitting(true)
    try {
      const result = await ClaimReservationsServiceAPI.unscheduleBookable(
        activeClaimCenter.id,
        unscheduleBookableId,
        convertDateToIsoDate(startDate),
      )
      if (result.failedReservations.length > 0) {
        const count = result.failedReservations.length
        setUnscheduleError(
          `${count} reservation${count !== 1 ? 's' : ''} could not be processed. The slot was not removed. Please handle them manually and try again.`,
        )
      } else {
        setUnscheduleBookableId(null)
        setUnschedulePreview(null)
      }
      refetchBookables()
      await refetchGrid()
    } catch {
      setUnscheduleError('Failed to remove slot. Please try again.')
    } finally {
      setIsUnscheduleSubmitting(false)
    }
  }

  const handleUnscheduleCancel = () => {
    setUnscheduleBookableId(null)
    setUnschedulePreview(null)
    setUnscheduleError(null)
  }

  const handlePlayerSearchResult = (result: ClaimReservationResponse) => {
    setShowPlayerSearch(false)

    if (gridDisplayMode !== GridDisplayMode.DAY) {
      onDisplayModeChange(GridDisplayMode.DAY)
    }

    if (result.claimCenter.id !== activeClaimCenter?.id) {
      const matching = claimCenters?.find((c) => c.id === result.claimCenter.id)
      if (matching) {
        onClaimCenterChange(matching)
      }
    }

    const resDate = parseISO(result.timeslot.display.localDate)
    onDateRangeChange(resDate)

    setPendingOpenReservationId(result.id)
  }

  const handleClaimFormClick = (reservationId: string) => {
    const reservation = reservationsData?.reservations.find((r) => r.id === reservationId)
    if (!reservation?.claimFormId) {
      return
    }

    if (drawerClaimFormId === reservation.claimFormId) {
      setDrawerClaimFormId(null)
      return
    }

    setClaimFormClaimantName(reservation.claimReservationDetails.name)

    if (selectedReservation) {
      setReopenReservationOnDrawerClose(selectedReservation)
      setSelectedReservation(null)
    }

    setDrawerClaimFormId(reservation.claimFormId)
  }

  const hasUnscheduledBookables = useMemo(() => {
    return !!(
      gridDisplayMode === GridDisplayMode.DAY &&
      datesForRange.length > 0 &&
      bookablesForDates &&
      bookablesForDates[datesForRange[0]].filter((b) => b.schedules.length === 0).length
    )
  }, [bookablesForDates, datesForRange, gridDisplayMode])

  const getDefaultDate = useCallback(() => {
    return isWithinInterval(getTodayAsUTC(), { start: startDate, end: endDate }) // If today is within what we're currently viewing, then init the modal with today. Otherwise init it to the first day visible
      ? getTodayAsUTC()
      : startDate
  }, [endDate, startDate])

  return (
    <Theme accentColor="yellow">
      <Flex direction="column" style={{ width: '100%' }}>
        <Flex justify="between" className="grid-controls">
          <Flex align="center" gap="3">
            <Text weight="bold" className="week-day-header" wrap="nowrap">
              {gridDisplayMode === GridDisplayMode.DAY
                ? formatDateToDayString(startDate)
                : `Week of ${formatWeekString(startDate, endDate)}`}
            </Text>
            <WithPermissions requiredPermissions={AuthScopes.writeclaim_center_reservations}>
              <Button
                className="control-button schedule-appointment"
                variant="outline"
                onClick={() => {
                  setShowScheduleAppointment(true)
                }}>
                Schedule Appointment
              </Button>
            </WithPermissions>
            <Text size="1" wrap="nowrap">
              <Em>{activeClaimCenter?.name} Claim Center</Em>
            </Text>
          </Flex>

          <Flex align="center">
            <DateControl
              startDate={startDate}
              endDate={endDate}
              gridDisplayMode={gridDisplayMode}
              onDateRangeChange={onDateRangeChange}
              onDisplayModeChange={onDisplayModeChange}
            />

            <WithPermissions requiredPermissions={AuthScopes.readclaim_center_reservations}>
              <Flex align="center" gap="3">
                <Separator orientation="vertical" size="2" />
                <IconButton
                  variant="ghost"
                  color="gray"
                  aria-label="Search player appointments"
                  onClick={() => setShowPlayerSearch(true)}
                  mr="2">
                  <MagnifyingGlassIcon height="18" width="18" />
                </IconButton>
              </Flex>
            </WithPermissions>

            {activeClaimCenter ? (
              <ActionMenu
                hasUnscheduledBookables={hasUnscheduledBookables}
                canRemoveSlot={canRemoveSlot}
                claimCenters={claimCenters ?? []}
                activeClaimCenter={activeClaimCenter}
                onAddAppointmentSlot={() => scheduleBookable()}
                onRemoveAppointmentSlot={handleRemoveAppointmentSlot}
                onScheduleUnavailability={() => setShowUnavailability(true)}
                onLocationSelection={onClaimCenterChange}
              />
            ) : null}
          </Flex>
        </Flex>

        {activeClaimCenter && activeCustomerId && startHour && endHour ? (
          <ReservationGrid
            dates={datesForRange}
            startHour={startHour}
            endHour={endHour}
            onReservationSelected={setSelectedReservation}
            onExceptionSelected={setSelectedException}
            onClaimFormClick={handleClaimFormClick}
            onGridCellClick={(gridCellProps: GridItemProps) => {
              onDisplayModeChange(GridDisplayMode.DAY)
              const clickedDate = parseISO(gridCellProps.channelUuid)
              onDateRangeChange(clickedDate)
            }}
            reservationsData={reservationsData}
            claimCenterBookablesByDates={bookablesForDates || {}}
            gridDataLoading={isReservationLoading || isBookableLoading}
            gridDisplayMode={gridDisplayMode}
          />
        ) : null}

        {selectedReservation ? (
          <ReservationDetails
            reservation={selectedReservation}
            claimCenterId={activeClaimCenter!.id}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                setSelectedReservation(null)
              }
            }}
            refetchGrid={refetchGrid}
            onClaimFormClick={handleClaimFormClick}
            onReservationUpdated={onReservationUpdated}
            onReservationRemoved={onReservationRemoved}
          />
        ) : null}

        {selectedException && startHour && endHour ? (
          <ExceptionDetails
            exception={selectedException}
            claimCenterId={activeClaimCenter!.id}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                setSelectedException(null)
              }
            }}
            refetchGrid={refetchGrid}
            claimCenterStartHour={startHour}
            claimCenterEndHour={endHour}
          />
        ) : null}

        {showUnavailability ? (
          <ScheduleUnavailability
            onOpenChange={setShowUnavailability}
            activeClaimCenter={activeClaimCenter}
            currentDate={getDefaultDate()}
            refetchGrid={refetchGrid}
          />
        ) : null}

        {showScheduleAppointment && activeClaimCenter ? (
          <ScheduleAppointment
            onOpenChange={setShowScheduleAppointment}
            claimCenterId={activeClaimCenter.id}
            refetchGrid={refetchGrid}
            defaultDate={getDefaultDate()}
          />
        ) : null}

        {unschedulePreview && unscheduleBookableId ? (
          <UnscheduleConfirmation
            preview={unschedulePreview}
            onConfirm={handleUnscheduleConfirm}
            onCancel={handleUnscheduleCancel}
            error={unscheduleError}
            isSubmitting={isUnscheduleSubmitting}
          />
        ) : null}

        {drawerClaimFormId ? (
          <ClaimFormDrawer
            key={drawerClaimFormId}
            claimFormId={drawerClaimFormId}
            claimantName={claimFormClaimantName}
            onClose={() => {
              setDrawerClaimFormId(null)
              if (reopenReservationOnDrawerClose) {
                setSelectedReservation(reopenReservationOnDrawerClose)
                setReopenReservationOnDrawerClose(null)
              }
            }}
            onAuthorized={() => {
              queryClient.setQueryData(scheduleQueryKey, (old: typeof reservationsData) => {
                if (!old) {
                  return old
                }
                return {
                  ...old,
                  reservations: old.reservations.map((r) =>
                    r.claimFormId === drawerClaimFormId ? { ...r, claimFormAuthorized: true } : r,
                  ),
                }
              })
            }}
            onLinkAppointment={(formId) => {
              const reservation = reservationsData?.reservations.find(
                (r) => r.claimFormId === formId,
              )
              setLinkModalState({
                claimFormId: formId,
                currentReservationId: reservation?.id ?? null,
              })
            }}
          />
        ) : null}

        {linkModalState ? (
          <LinkAppointmentModal
            claimFormId={linkModalState.claimFormId}
            currentReservationId={linkModalState.currentReservationId}
            onSuccess={() => {
              setLinkModalState(null)
              refetchGrid()
            }}
            onClose={() => setLinkModalState(null)}
          />
        ) : null}

        {showPlayerSearch ? (
          <PlayerSearchModal
            onResultSelected={handlePlayerSearchResult}
            onClose={() => setShowPlayerSearch(false)}
          />
        ) : null}
      </Flex>
    </Theme>
  )
}
